import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,

} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import { useRef, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';

export default function UserDialog({ openDialog, handleCloseDialog, user }) {
  const S3_URL = process.env.REACT_APP_S3_URL;
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const roles = [
    { value: "admin", label: "Admin" },
    { value: "seller", label: "Vendedor" },
    { value: "manager", label: "Gerente" },
  ];

  const [formData, setFormData] = useState({
    name: '',
    avatar_url: '',
    password: '',
    start_date: null,
    end_date: null,
    role: 'seller',
    pdv_code: '',
  });

  useEffect(() => {
    if (user && openDialog) {
      setFormData({
        name: user.name || '',
        avatar_url: user.avatar_url || '',
        password: '',
        start_date: user.start_date ? dayjs(user.start_date) : null,
        end_date: user.end_date ? dayjs(user.end_date) : null,
        role: user.role || 'seller',
        pdv_code: user.pdv_code || '',
      });
      setAvatarPreview(null);
    } else {
      setFormData({
        name: '',
        avatar_url: '',
        password: '',
        start_date: null,
        end_date: null,
        role: 'seller',
        pdv_code: '',
      });
      setAvatarPreview(null);
    }
  }, [user, openDialog]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, avatar_url: file }));
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    setIsLoading(true);
    const fData = new FormData();
    fData.append('name', formData.name);
    fData.append('password', formData.password);
    fData.append('start_date', formData.start_date?.format('YYYY-MM-DD') || '');
    fData.append('end_date', formData.end_date?.format('YYYY-MM-DD') || '');
    fData.append('role', formData.role);
    fData.append('pdv_code', formData.pdv_code);
    if (formData.avatar_url instanceof File) {
      fData.append('avatar', formData.avatar_url);
    }

    try {
      const endpoint = user ? `${API_BASE_URL}user/${user.uuid}` : `${API_BASE_URL}user`;
      const method = user ? 'put' : 'post';

      await axios({
        method,
        url: endpoint,
        data: fData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('Usuário salvo com sucesso!');
      handleCloseDialog();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao salvar usuário');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
      <DialogTitle>Gerenciar Usuário</DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" alignItems="center" mt={1} mb={2}>
          <Avatar
            src={
              avatarPreview
                ? avatarPreview
                : formData.avatar_url && !(formData.avatar_url instanceof File)
                ? `${S3_URL}/img/user/${formData.avatar_url}`
                : undefined
            }
            sx={{ width: 80, height: 80, mb: 1 }}
            onClick={handleAvatarClick}
          />
          <IconButton color="primary" component="label" onClick={handleAvatarClick}>
            <PhotoCamera />
            <input hidden ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} />
          </IconButton>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Nome"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              autoComplete="off"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Senha"
              name="password"
              type="password"
              value={formData.password}
              required={!!user}
              onChange={handleChange}
              autoComplete="new-password"
            />
          </Grid>

          <Grid item xs={6}>
            <DatePicker
              label="Data Inicial *"
              value={formData.start_date}
              onChange={(newValue) => setFormData((prev) => ({ ...prev, start_date: newValue }))}
              renderInput={(params) => <TextField fullWidth {...params} />}
            />
          </Grid>

          <Grid item xs={6}>
            <DatePicker
              label="Data Final"
              value={formData.end_date}
              onChange={(newValue) => setFormData((prev) => ({ ...prev, end_date: newValue }))}
              renderInput={(params) => <TextField fullWidth {...params} />}
            />
          </Grid>

          <Grid item xs={6}>
            <FormControl required fullWidth>
              <InputLabel id="role-label">Tipo</InputLabel>
              <Select
                labelId="role-label"
                value={formData.role || ""}
                onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
              >
                {roles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Código PDV"
              name="pdv_code"
              value={formData.pdv_code}
              onChange={handleChange}
              required
              autoComplete="off"
            />
          </Grid>
        </Grid>

      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCloseDialog} color="secondary">Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading}
          startIcon={isLoading && <CircularProgress size={20} />}
        >
          {isLoading ? 'Salvando...' : 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
